import React, { useMemo } from 'react';
import { StickyNote } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

const typeLabel = (type) => ({
  user: 'User',
  team: 'Team',
  crew: 'Crew',
  department: 'Department',
}[type] || 'Candidate');

const mergeTargets = (...groups) => {
  const seen = new Set();
  return groups.flat().filter((row) => {
    const key = `${row?.type || ''}:${row?.id || ''}`;
    if (!row?.type || !row?.id || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// Compact, read-only candidate coverage. Candidate editing belongs exclusively
// to injected-card modals, and saved cards are the source of this summary.
export default function StaffingPlanEditor({ roles = [], initialPlan, instructionsByRole = {}, dark = false }) {
  const normalizedRoles = useMemo(() => {
    const seen = new Set();
    return (roles || []).reduce((result, entry) => {
      const role = String(entry?.role || '').trim();
      if (!role || seen.has(role)) return result;
      seen.add(role);
      result.push(role);
      return result;
    }, []);
  }, [roles]);

  const shell = dark ? 'border-slate-700 bg-slate-950/60' : 'border-slate-200 bg-white';
  const header = dark ? 'border-slate-700 bg-slate-900/70' : 'border-slate-200 bg-slate-50/80';
  const divider = dark ? 'divide-slate-700' : 'divide-slate-100';
  const text = dark ? 'text-slate-100' : 'text-slate-800';
  const muted = dark ? 'text-slate-400' : 'text-slate-500';

  return (
    <div className={`overflow-hidden rounded-lg border ${shell}`} data-testid="staffing-plan-editor">
      <div className={`flex items-center justify-between gap-3 border-b px-3 py-2 ${header}`}>
        <div className="min-w-0">
          <p className={`text-[10px] font-bold uppercase tracking-wider ${text}`}>Default staffing</p>
          <p className={`mt-0.5 text-[10px] ${muted}`}>Candidates sync from saved cards. Manage them inside each card.</p>
        </div>
        <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide ${dark ? 'border-slate-600 text-slate-300' : 'border-slate-200 bg-white text-slate-500'}`}>
          Read only
        </span>
      </div>

      <div className={`divide-y ${divider}`}>
        {normalizedRoles.length === 0 && (
          <div className="px-3 py-4 text-center" data-testid="staffing-plan-empty">
            <p className={`text-[11px] font-semibold ${text}`}>No roles on saved cards.</p>
            <p className={`mt-0.5 text-[10px] ${muted}`}>A role appears here after it is saved on an injected card.</p>
          </div>
        )}
        {normalizedRoles.map((role) => {
          const config = initialPlan?.roles?.[role] || {};
          const instructions = Array.isArray(instructionsByRole?.[role])
            ? instructionsByRole[role].filter((entry) => String(entry?.text || '').trim())
            : [];
          const candidates = mergeTargets(
            Array.isArray(config.preferred) ? config.preferred : [],
            Array.isArray(config.fallbacks) ? config.fallbacks : [],
          );
          return (
            <div key={role} className="px-3 py-2.5" data-testid={`staffing-role-summary-${role}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-1.5">
                  <p className={`min-w-0 truncate text-xs font-bold ${text}`}>{role}</p>
                  {instructions.length > 0 && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          aria-label={`Show assignment instructions for ${role}`}
                          title="Assignment instructions"
                          className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors ${dark ? 'border-slate-600 text-cyan-400 hover:bg-slate-800' : 'border-violet-200 bg-violet-50 text-violet-600 hover:bg-violet-100'}`}
                          data-testid={`staffing-role-instructions-${role}`}
                        >
                          <StickyNote className="h-3 w-3" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        align="start"
                        sideOffset={6}
                        className={`w-[min(22rem,calc(100vw-2rem))] space-y-3 p-3 ${dark ? 'border-slate-700 bg-slate-900 text-slate-100' : 'border-violet-200 bg-white text-slate-800'}`}
                        data-testid={`staffing-role-instructions-popover-${role}`}
                      >
                        <div>
                          <p className={`text-[10px] font-bold uppercase tracking-wider ${dark ? 'text-cyan-400' : 'text-violet-700'}`}>Assignment instructions</p>
                          <p className={`mt-0.5 text-xs font-bold ${text}`}>{role}</p>
                        </div>
                        <div className="space-y-2">
                          {instructions.map((entry, index) => (
                            <div key={`${entry.text}-${index}`} className={`rounded-md border px-2.5 py-2 ${dark ? 'border-slate-700 bg-slate-950/60' : 'border-violet-100 bg-violet-50/50'}`}>
                              <p className={`text-[11px] leading-4 ${text}`}>{entry.text}</p>
                              {Array.isArray(entry.sources) && entry.sources.length > 0 && (
                                <p className={`mt-1 text-[9px] font-semibold ${muted}`}>
                                  {entry.sources.map((source) => source.label).filter(Boolean).join(' · ')}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
                <span className={`shrink-0 text-[10px] font-medium ${muted}`}>
                  {candidates.length} {candidates.length === 1 ? 'candidate' : 'candidates'}
                </span>
              </div>

              {candidates.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`staffing-role-candidates-${role}`}>
                  {candidates.map((candidate) => (
                    <span
                      key={`${candidate.type}:${candidate.id}`}
                      className={`inline-flex max-w-full items-center gap-1.5 rounded-full border px-2 py-1 ${dark ? 'border-slate-700 bg-slate-900' : 'border-slate-200 bg-slate-50'}`}
                    >
                      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-violet-500" />
                      <span className={`max-w-[12rem] truncate text-[10px] font-semibold ${text}`}>{candidate.name || candidate.id}</span>
                      <span className={`text-[8px] font-bold uppercase tracking-wide ${muted}`}>{typeLabel(candidate.type)}</span>
                    </span>
                  ))}
                </div>
              ) : (
                <p className={`mt-1 text-[10px] ${muted}`}>No candidates on saved cards.</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
